import useModal from "./modalHook";
import { Link } from "react-router-dom";
import useRoute from "./routesHook";
import MarkCommissionAsPaidModal from "../views/partners/shared/commissions/MarkCommissionAsPaidModal";
import { useMemo } from "react";
import { createSelectionColumn } from "../components/elements/GTable/GTable";
import usePolicy from "./policyHook";
import { formatCurrencyAmount, formatPercentageValue, getFormattedDate } from "../utils/miscFunctions";
import IconStatus from "../components/elements/IconStatus";
import PopOnMe from "../components/elements/poponme/PopOnMe";
import { EllipsisVerticalIcon } from "lucide-react";

type UseCommissionsProps = {
    getTableRowActions?: (row: any) => any[];
    showCheckColumn?: boolean;
}

export function useCommissions({ getTableRowActions, showCheckColumn = true }: UseCommissionsProps) {

    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const CommissionsModal = ModalComponent;
    const { getDashboardBase } = useRoute();
    const { PolicyTypesBadge } = usePolicy()

    const handleMarkCommissionAsPaidAndSettle = (commissions: any) => {
        updateModalStates({
            show: true,
            children: (
                <MarkCommissionAsPaidModal
                    onClose={closeModal}
                    commissions={commissions}
                />
            ),
            static: true,
            size: '7xl'
        });
    }

    const getPolicyDetailsRouteFromCommission = (commission: any) => {
        if (!commission.policyNumber) return;

        const isVehicle = commission.policyType === 'VEHICLE';

        if (isVehicle) {
            return `${getDashboardBase()}/policies/${commission.policyNumber}?type=motor`;

        } else {
            return `${getDashboardBase()}/policies/${commission.policyNumber}?type=non-motor`;
        }
    }

    const columns = useMemo(
        () => {
            return [
                {
                    Header: 'M',
                    columns: [
                        ...(showCheckColumn ? [
                            createSelectionColumn(),
                        ] : []),
                        {
                            Header: 'Customer Name',
                            accessor: 'customerName',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Policy Type',
                            accessor: 'policyType',
                            Cell: (row: any) => {
                                return row.cell.value ? <PolicyTypesBadge policyType={row.cell.value} /> : '-';
                            }
                        },
                        {
                            Header: 'Policy ID',
                            accessor: 'policyNumber',
                            Cell: (row: any) => {
                                const route = getPolicyDetailsRouteFromCommission(row?.row?.original) || '';
                                return (
                                    row.cell.value ?
                                        <Link
                                            to={route} target="_blank">
                                            <span className="hover:underline  dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                                        </Link> : '-'
                                )
                            }
                        },
                        {
                            Header: 'Currency',
                            accessor: 'currency',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Gross Premium',
                            accessor: 'grossPremiumAmount',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Net Premium',
                            accessor: 'netPremiumAmount',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Com Rate (%)',
                            accessor: 'rate',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatPercentageValue(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Gross Com',
                            accessor: 'grossCommissionAmount',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'WHT',
                            accessor: 'withholdingTax',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatPercentageValue(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Net Com',
                            accessor: 'netCommissionAmount',
                            Cell: (row: any) => {
                                return <span>{row.cell.value ? formatCurrencyAmount(row.cell.value) : '-'}</span>;
                            }
                        },
                        {
                            Header: 'Paid',
                            accessor: 'isPaid',
                            Cell: (row: any) => {
                                return <IconStatus status={row.cell.value} />;
                            }
                        },
                        {
                            Header: 'Inception Date',
                            accessor: 'createdOn',
                            Cell: (row: any) => {
                                return (
                                    <span>
                                        {row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}
                                    </span>
                                );
                            }
                        },
                        {
                            Header: 'Sale Date',
                            accessor: 'dateOfPayment',
                            Cell: (row: any) => {
                                return (
                                    <span>
                                        {row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}
                                    </span>
                                );
                            }
                        },
                        ...(getTableRowActions ? [{
                            Header: "Actions",
                            id: 'actions',
                            Cell: (row: any) => (
                                <div className="cursor-pointer flex justify-center">
                                    {getTableRowActions(row.row.original).length > 0 && (
                                        <PopOnMe actions={getTableRowActions(row.row.original)}>
                                            <EllipsisVerticalIcon className="h-4 w-4" />
                                        </PopOnMe>
                                    )}
                                </div>)
                        }] : [])
                    ]
                }
            ]
        },
        []
    );


    return {
        handleMarkCommissionAsPaidAndSettle,
        CommissionsModal,
        columns
    }
}
